// Shared line chart for the General Report sections.
//
// Series in, chart out. The three report sections differ only in the series they
// hand this component, so all the drawing rules live here in one place:
//
//   - ONE y-axis, always. Never a second scale.
//   - Colour follows the entity, not its rank (see buildColorMap) - changing the
//     date range must not repaint the surviving series.
//   - Legend carries series identity. Lines are NOT labelled at their right-hand
//     end: the names ran past the plot edge and were clipped mid-word, which read
//     worse than no label at all. The relief rule below is satisfied by the Table
//     toggle, so dropping the end labels costs nothing in accessibility terms.
//   - Crosshair + one shared tooltip listing every series at the hovered date,
//     sorted high to low.
//   - The Table toggle is not optional chrome: three palette slots sit below 3:1
//     contrast on white, and the validator's relief rule requires either visible
//     labels or a table view. See vizPalette.js.
import React, { useState } from 'react';
import {
    Box, Paper, Typography, ToggleButton, ToggleButtonGroup, CircularProgress, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip, ResponsiveContainer,
} from 'recharts';
import { CHART_CHROME, OTHER_COLOR, OTHER_KEY } from './vizPalette';
import { formatDayLabel } from './chartFormat';

const tickStyle = {
    fill: CHART_CHROME.mutedInk,
    fontSize: 11,
    fontVariantNumeric: 'tabular-nums',
};

// Shared tooltip: every series at the hovered date, biggest first.
function CrosshairTooltip({ active, payload, label, colorMap }) {
    if (!active || !payload || !payload.length) return null;
    const rows = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
    const total = rows.reduce((sum, r) => sum + (r.value || 0), 0);
    return (
        <Paper elevation={3} sx={{ p: 1.25, minWidth: 190 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: CHART_CHROME.primaryInk }}>
                {formatDayLabel(label)}
            </Typography>
            <Box sx={{ mt: 0.75 }}>
                {rows.map(r => (
                    <Box key={r.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Box sx={{
                            width: 10, height: 10, borderRadius: '2px', flexShrink: 0,
                            bgcolor: (colorMap && colorMap[r.dataKey]) || r.color,
                        }} />
                        <Typography variant="caption" sx={{ flexGrow: 1, color: CHART_CHROME.secondaryInk }}>
                            {r.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {r.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Box sx={{
                mt: 0.75, pt: 0.5, borderTop: `1px solid ${CHART_CHROME.grid}`,
                display: 'flex', justifyContent: 'space-between',
            }}>
                <Typography variant="caption" sx={{ color: CHART_CHROME.mutedInk }}>Total</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {total}
                </Typography>
            </Box>
        </Paper>
    );
}

// Per-entity summary. Its job is to reveal what got folded into "Other" and to
// satisfy the contrast relief rule - so it lists ALL series, untruncated.
function SummaryTable({ allSeries, dayCount, entityLabel }) {
    const grandTotal = allSeries.reduce((sum, s) => sum + s.total, 0);
    return (
        <TableContainer sx={{ maxHeight: 420, overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>{entityLabel}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total scans</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Share</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Daily avg</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Peak day</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {allSeries.map(s => {
                        const peak = s.points.reduce(
                            (best, p) => (p.count > best.count ? p : best),
                            { date: null, count: 0 },
                        );
                        const share = grandTotal > 0 ? Math.round((s.total / grandTotal) * 100) : 0;
                        const avg = dayCount > 0 ? (s.total / dayCount).toFixed(1) : '0.0';
                        return (
                            <TableRow key={s.key} hover>
                                <TableCell>{s.label}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.total}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{share}%</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{avg}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {peak.count > 0 ? `${peak.count} on ${formatDayLabel(peak.date)}` : '—'}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {allSeries.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">No data.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function ScanTrendChart({
    title, data, loading, error, errorMessage, onRetry, colorMap,
    entityLabel = 'Series', height = 320,
}) {
    const [view, setView] = useState('chart');

    const series = (data && data.series) || [];
    const allSeries = (data && data.allSeries) || [];
    const rows = (data && data.rows) || [];
    const days = (data && data.days) || [];
    const foldedCount = (data && data.foldedCount) || 0;

    // Assigned by the page via a stable slot allocator, so a series keeps its
    // colour when the date range changes. See vizPalette.createSlotAllocator.
    const colors = colorMap || {};
    const colorFor = key => (key === OTHER_KEY ? OTHER_COLOR : colors[key]) || OTHER_COLOR;
    const hasAnyScans = series.some(s => s.total > 0);

    function body() {
        if (loading) {
            return (
                <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">Loading…</Typography>
                </Box>
            );
        }
        if (error) {
            return (
                <Box sx={{
                    height, display: 'flex', flexDirection: 'column', gap: 0.5,
                    alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 2,
                }}>
                    <Typography color="error" variant="body2">Failed to load.</Typography>
                    {errorMessage && (
                        <Typography
                            variant="caption"
                            sx={{ color: CHART_CHROME.mutedInk, maxWidth: 560, wordBreak: 'break-word' }}
                        >
                            {errorMessage}
                        </Typography>
                    )}
                    {onRetry && <Button size="small" onClick={onRetry}>Retry</Button>}
                </Box>
            );
        }
        if (view === 'table') {
            return <SummaryTable allSeries={allSeries} dayCount={days.length} entityLabel={entityLabel} />;
        }
        if (!rows.length || !hasAnyScans) {
            return (
                <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No scans in this date range.
                    </Typography>
                </Box>
            );
        }
        return (
            <ResponsiveContainer width="100%" height={height}>
                {/* The old right margin of 96 existed only to make room for the
                    end labels; without them the plot reclaims that width. */}
                <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke={CHART_CHROME.grid} strokeDasharray="0" vertical={false} />
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatDayLabel}
                        tick={tickStyle}
                        stroke={CHART_CHROME.axis}
                        tickLine={false}
                        minTickGap={24}
                    />
                    <YAxis
                        allowDecimals={false}
                        tick={tickStyle}
                        stroke={CHART_CHROME.axis}
                        tickLine={false}
                        axisLine={false}
                        width={44}
                    />
                    <Tooltip
                        content={<CrosshairTooltip colorMap={colors} />}
                        cursor={{ stroke: CHART_CHROME.axis, strokeWidth: 1 }}
                    />
                    <Legend
                        verticalAlign="bottom"
                        iconType="plainline"
                        wrapperStyle={{ fontSize: 12, color: CHART_CHROME.secondaryInk, paddingTop: 8 }}
                    />
                    {series.map(s => {
                        const color = colorFor(s.key);
                        return (
                            <Line
                                key={s.key}
                                type="monotone"
                                dataKey={s.key}
                                name={s.label}
                                stroke={color}
                                strokeWidth={2}
                                strokeDasharray={s.key === OTHER_KEY ? '4 3' : undefined}
                                dot={false}
                                activeDot={{ r: 4, strokeWidth: 2 }}
                                isAnimationActive={false}
                            />
                        );
                    })}
                </LineChart>
            </ResponsiveContainer>
        );
    }

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 2, mb: 1, flexWrap: 'wrap',
            }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={view}
                    onChange={(e, v) => v && setView(v)}
                    aria-label="chart or table view"
                >
                    <ToggleButton value="chart" aria-label="chart view">Chart</ToggleButton>
                    <ToggleButton value="table" aria-label="table view">Table</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {body()}

            {!loading && !error && view === 'chart' && foldedCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Top {series.length - 1} shown · {foldedCount} more grouped as “Other” — switch to
                    Table for the full breakdown.
                </Typography>
            )}
        </Paper>
    );
}
